import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "./categories.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import ImageUpload from "../../components/imageUpload/imageUpload";
import NotificationBell from "../../components/notificationBell/notificationBell";
import dayjs from "dayjs";

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    getCategories();
  }, []);

  const getCategories = () => {
    let getRes = (res) => {
      if (res.status === 200) {
        // Handle response structure: res.data.data.data for moment-category API
        const categoriesData = res.data?.data?.data || res.data?.data || res.data || [];
        setAllCategories(categoriesData);
        if (searchText) {
          const lower = searchText.toLowerCase();
          setCategories(
            categoriesData.filter((cat) =>
              (cat.title || "").toLowerCase().includes(lower)
            )
          );
        } else {
          setCategories(categoriesData);
        }
      }
    };

    callApi(
      "GET",
      routes.getAllMomentCategories,
      null,
      setIsLoading,
      getRes,
      (error) => {
        message.error("Failed to load categories");
      }
    );
  };

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      title: category.title,
      image: category.image,
      icon: category.icon,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (categoryId) => {
    let getRes = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success("Category deleted successfully");
        getCategories();
      } else {
        message.warning(`Delete completed with status ${res.status}`);
        getCategories();
      }
    };

    callApi(
      "DELETE",
      `${routes.deleteMomentCategory}/${categoryId}`,
      null,
      setIsLoading,
      getRes,
      (error) => {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete category";
        message.error(errorMsg);
      }
    );
  };

  const handleSubmit = (values) => {
    // Close modal first, then proceed with API call
    setIsModalVisible(false);
    form.resetFields();
    const currentEditingCategory = editingCategory;
    setEditingCategory(null);
    
    if (currentEditingCategory) {
      // Update
      let getRes = (res) => {
        if (res.status === 200 || res.status === 201) {
          message.success("Category updated successfully");
          getCategories();
        } else {
          message.warning(`Update completed with status ${res.status}`);
          getCategories();
        }
      };

      callApi(
        "PATCH",
        `${routes.updateMomentCategory}/${currentEditingCategory._id}`,
        values,
        setIsLoading,
        getRes,
        (error) => {
          const errorMsg = error?.response?.data?.message || error?.message || "Failed to update category";
          message.error(errorMsg);
        }
      );
    } else {
      // Create
      let getRes = (res) => {
        if (res.status === 200 || res.status === 201) {
          message.success("Category created successfully");
          getCategories();
        } else {
          message.warning(`Create completed with status ${res.status}`);
          getCategories();
        }
      };

      callApi(
        "POST",
        routes.createMomentCategory,
        values,
        setIsLoading,
        getRes,
        (error) => {
          const errorMsg = error?.response?.data?.message || error?.message || "Failed to create category";
          message.error(errorMsg);
        }
      );
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingCategory(null);
  };

  const handleSearch = (value) => {
    const text = value || "";
    setSearchText(text);

    if (!text) {
      setCategories(allCategories);
      return;
    }

    const lower = text.toLowerCase();
    setCategories(
      allCategories.filter((cat) =>
        (cat.title || "").toLowerCase().includes(lower)
      )
    );
  };


  const columns = [
    {
      title: "No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 60,
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 100,
      render: (image) => (
        <Image
          src={image}
          alt="Category"
          width={60}
          height={60}
          style={{ objectFit: "cover", borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29BwNZwQxT0g2F4U0B0J9BsKxO4L4J4BdGTGMjQCYQe4B"
        />
      ),
    },
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      width: 100,
      render: (icon) => (
        icon ? (
          <Image
            src={icon}
            alt="Category Icon"
            width={40}
            height={40}
            style={{ objectFit: "cover", borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29BwNZwQxT0g2F4U0B0J9BsKxO4L4J4BdGTGMjQCYQe4B"
          />
        ) : (
          <span style={{ color: "#999" }}>N/A</span>
        )
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => <span style={{ fontWeight: 500, color: "#333" }}>{text || "N/A"}</span>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "N/A"),
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Category"
            description="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="categories-container">
        <Loader loading={isLoading} />
        <div className="categories-header">
          <h1 className="categories-title">Category Management</h1>
          <NotificationBell />
        </div>

        <div className="categories-content">
          <Card className="categories-card">
            <div className="categories-actions">
              <Input
                placeholder="Search categories by title..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{ maxWidth: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                size="middle"
              >
                Add Category
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={categories}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} categories`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              className="categories-table"
            />
          </Card>
        </div>

        <Modal
          title={editingCategory ? "Edit Category" : "Add New Category"}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="category-form"
          >
            <Form.Item
              label="Category Title"
              name="title"
              rules={[
                { required: true, message: "Please enter category title" },
                { min: 2, message: "Title must be at least 2 characters" },
                { max: 50, message: "Title must be less than 50 characters" },
              ]}
            >
              <Input placeholder="Enter category title" size="large" />
            </Form.Item>

            <Form.Item
              label="Category Image"
              name="image"
              rules={[{ required: true, message: "Please upload category image" }]}
            >
              <ImageUpload maxSize={5} />
            </Form.Item>

            <Form.Item
              label="Category Icon"
              name="icon"
              rules={[{ required: true, message: "Please upload category icon" }]}
            >
              <ImageUpload maxSize={5} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" size="large">
                  {editingCategory ? "Update" : "Create"}
                </Button>
                <Button onClick={handleCancel} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default Categories;
